import React from 'react';
import styles from './index.module.scss';
import classNames from 'classnames';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { livePhotoImage } from '@images/imageUrls';
import { getProtectedEventHandlers, isContentProtectionEnabled, protectedMediaStyle } from '@utils/contentProtection';

interface ImageItem {
  id: number;
  url: string;
  webpUrl?: string;
  isLive?: boolean;
  liveUrl?: string;
  fileWidth?: number;
  fileHeight?: number;
  fileName?: string;
}

interface LivePhotoPreviewProps {
  visible: boolean;
  imageList: ImageItem[];
  initialIndex?: number;
  onClose: () => void;
  allowDownload?: boolean;
}

interface LivePhotoPreviewState {
  activeIndex: number;
  muted: boolean;
  isVideoPlaying: boolean;
  isVivoBrowserStatus: boolean
}

class LivePhotoPreview extends React.Component<LivePhotoPreviewProps, LivePhotoPreviewState> {
  private videoRefs: Map<number, HTMLVideoElement> = new Map();
  private swiperInstance: any = null;

  constructor(props: LivePhotoPreviewProps) {
    super(props);
    this.state = {
      activeIndex: props.initialIndex || 0,
      muted: true,
      isVideoPlaying: false,
      isVivoBrowserStatus: false, // 是否是vivo浏览器
    };
  }

  componentDidMount() {
    this.isVivoBrowser()
  }

  componentDidUpdate(prevProps: LivePhotoPreviewProps) {
    if (!prevProps.visible && this.props.visible) {
      this.setState({
        activeIndex: this.props.initialIndex || 0,
        muted: true,
        isVideoPlaying: false,
      });
      document.body.style.overflow = 'hidden';
      // 如果初始就是 live 图，自动播放
      setTimeout(() => {
        this.autoPlayCurrentLive(this.props.initialIndex || 0);
      }, 300);
    }
    if (prevProps.visible && !this.props.visible) {
      document.body.style.overflow = '';
      this.pauseAllVideos();
    }
    
  }

  componentWillUnmount() {
    document.body.style.overflow = '';
    this.pauseAllVideos();
  }

  pauseAllVideos = () => {
    this.videoRefs.forEach((video) => {
      if (video && !video.paused) {
        video.pause();
      }
    });
    this.setState({ isVideoPlaying: false });
  };

  /**
     * 判断当前浏览器是否为 vivo 浏览器
     * @returns {boolean} true: 是 vivo 浏览器, false: 不是
     */
    isVivoBrowser = () => {
        if (typeof navigator === 'undefined') {
            return false;
        }
        
        const ua = navigator.userAgent.toLowerCase();
        // 优先判断 VivoBrowser 关键词，其次判断 vivo 关键词作为兼容
        let isVivoBrowser = /vivobrowser/i.test(ua) || 
            ( /vivo/i.test(ua) && !/micromessenger/i.test(ua) && !/qq/i.test(ua) );
            this.setState({
                isVivoBrowserStatus: isVivoBrowser
            })
        return /vivobrowser/i.test(ua) || 
            ( /vivo/i.test(ua) && !/micromessenger/i.test(ua) && !/qq/i.test(ua) );
            
    }

  autoPlayCurrentLive = (index: number) => {
    const { imageList } = this.props;
    const currentImage = imageList[index];
    if (currentImage?.isLive && currentImage?.liveUrl) {
      const video = this.videoRefs.get(index);
      if (video) {
        video.currentTime = 0;
        video.muted = this.state.muted;
        video.play().then(() => {
          this.setState({ isVideoPlaying: true });
        }).catch(() => {});
      }
    }
  };

  handleSlideChange = (swiper: any) => {
    this.pauseAllVideos();
    const newIndex = swiper.activeIndex;
    this.setState({ activeIndex: newIndex }, () => {
      this.autoPlayCurrentLive(newIndex);
    });
  };

  toggleMute = (e: React.MouseEvent) => {
    e.stopPropagation();
    const { activeIndex } = this.state;
    const newMuted = !this.state.muted;
    this.setState({ muted: newMuted });
    const video = this.videoRefs.get(activeIndex);
    if (video) {
      video.muted = newMuted;
    }
  };

  handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    this.props.onClose();
  };

  handleVideoEnded = (index: number) => {
    // 视频播放结束，显示静态图
    this.setState({ isVideoPlaying: false });
  };

  setVideoRef = (index: number, el: HTMLVideoElement | null) => {
    if (el) {
      this.videoRefs.set(index, el);
    } else {
      this.videoRefs.delete(index);
    }
  };

  render() {
    const { visible, imageList, allowDownload } = this.props;
    const { activeIndex, muted, isVideoPlaying, isVivoBrowserStatus } = this.state;
    const isProtected = isContentProtectionEnabled(allowDownload);
    const protectedHandlers = getProtectedEventHandlers(isProtected);

    if (!visible || !imageList || imageList.length === 0) return null;

    const currentImage = imageList[activeIndex];
    const isCurrentLive = currentImage?.isLive && currentImage?.liveUrl;

    return (
      <div className={styles.overlay} style={{ background: '#fff' }} {...protectedHandlers}>
        {/* 顶部栏 */}
        <div className={styles.topBar}>
          <div className={styles.backBtn} onClick={this.handleClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M15 18L9 12L15 6" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className={styles.pageIndicator}>
            {activeIndex + 1}/{imageList.length}
          </div>
          <div className={styles.placeholder}></div>
        </div>

        {/* Live 图标签 */}
        {isCurrentLive && (
          <div className={styles.liveTagBar}>
            <div className={styles.liveTag} style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); this.autoPlayCurrentLive(activeIndex); }}>
              <img className={styles.liveIcon} src={livePhotoImage.liveIcon} alt="" />
              <span>动态照片</span>
            </div>
            <div className={styles.soundBtn} onClick={!isVivoBrowserStatus && this.toggleMute}>
              {muted ? (
                <img className={styles.soundIcon} src={livePhotoImage.volumeClose} alt="" />
              ) : (
                <img className={styles.soundIcon} src={livePhotoImage.volume} alt="" />
              )}
            </div>
          </div>
        )}

        {/* 内容区域 - Swiper */}
        <div className={styles.content}>
          <Swiper
            modules={[Navigation, Pagination]}
            initialSlide={this.props.initialIndex || 0}
            onSlideChange={this.handleSlideChange}
            onSwiper={(swiper) => { this.swiperInstance = swiper; }}
            className={styles.swiper}
          >
            {imageList.map((image, index) => (
              <SwiperSlide key={image.id || index}>
                <div className={styles.slideItem}>
                  {/* Live 图：视频 + 封面图 */}
                  {image.isLive && image.liveUrl && !isVivoBrowserStatus ? (
                    <div className={styles.liveContainer}>
                      <video
                        ref={(el) => this.setVideoRef(index, el)}
                        src={image.liveUrl}
                        className={styles.liveVideo}
                        playsInline
                        webkit-playsinline="true"
                        x5-playsinline="true"
                        x5-video-player-type="h5"
                        x5-video-player-fullscreen="false"
                        loop={false}
                        muted={muted}
                        onEnded={() => this.handleVideoEnded(index)}
                        poster={image.url || image.webpUrl}
                        style={isProtected ? protectedMediaStyle : undefined}
                        {...protectedHandlers}
                      />
                      {/* 视频没播放时显示封面图 */}
                      {!isVideoPlaying && activeIndex === index && (
                        <img
                          src={image.url || image.webpUrl}
                          className={styles.liveCoverImage}
                          alt=""
                          style={isProtected ? protectedMediaStyle : undefined}
                          {...protectedHandlers}
                        />
                      )}
                    </div>
                  ) : (
                    /* 普通图片 */
                    <img
                      src={image.url || image.webpUrl}
                      className={styles.previewImage}
                      alt={image.fileName || ''}
                      style={isProtected ? protectedMediaStyle : undefined}
                      {...protectedHandlers}
                    />
                  )}
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
    );
  }
}

export default LivePhotoPreview;
